import { useMarkAppointmentCompletedMutation } from '../../generated/graphql';

import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { logger } from '../../datadog/logger';
import { useAppDependenciesContext } from '../../state';
import { markAppointmentCompletedMutationGate } from '../../statsig/gates';
import { normalizeError } from '../../utils';
import type { RoomDelegator } from '../../video-sdk';
import { ProviderState, inSessionPatientInformationAtom, providerStateAtom } from '../state';
import { useProviderDisconnectRoomCallback } from '../useProviderDisconnectRoomCallback';
import { useCompleteTwilioSessionMutation } from './useCompleteTwilioSessionMutation';

export type CompleteSessionCallback = () => Promise<RoomDelegator | undefined>;

/**
 * @deprecated
 * Returns a callback function that completes the current Twilio session on the
 * server side. Will provide a strong guarantee that the session is completed,
 * but will take more time than a simple `room.disconnect()` call.
 *
 * Should be used as a fire-and-forget side-effect after a `room.disconnect()`
 * call.
 */
function useCompleteSessionSideEffectCallback() {
  const [completeSession] = useCompleteTwilioSessionMutation();

  return useCallback(
    async (room: RoomDelegator) => {
      try {
        await completeSession({
          variables: { roomSid: room.sid },
        });
        // Ensures that the room completion reason is set to "disconnected_via_api"
        room.disconnect();
        return room;
      } catch (error) {
        logger.error('Unable to complete session via GraphQL', {}, error as Error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}

/**
 * Returns a callback that handles all fire-and-forget, session completion
 * side-effects e.g. marking the appointment as completed.
 */
function useHandleSessionCompletionSideEffectsCallback() {
  const [markAppointmentCompletedMutation] = useMarkAppointmentCompletedMutation();
  const { logger } = useAppDependenciesContext();

  return useCallback(
    async (appointmentShortId?: string) => {
      if (!appointmentShortId) {
        logger.error('Unable to mark appointment as completed: missing appointment short ID');
        return;
      }
      try {
        await markAppointmentCompletedMutation({
          variables: {
            appointmentShortId,
          },
        });
      } catch (error) {
        logger.error('Failed to mark appointment as completed', { appointmentShortId }, normalizeError(error));
      }
    },
    [logger, markAppointmentCompletedMutation],
  );
}

/**
 * Returns a callback function that completes the current Twilio session and
 * sets the provider state to completed.
 */
export function useCompleteSessionCallback(): CompleteSessionCallback {
  const disconnectRoom = useProviderDisconnectRoomCallback();
  const setProviderState = useSetAtom(providerStateAtom);
  const dispatchSessionCompletionSideEffect = useCompleteSessionSideEffectCallback();
  const getInSessionAppointmentShortId = useAtomCallback(
    useCallback((get) => get(inSessionPatientInformationAtom)?.inSessionAppointmentShortId, []),
  );
  const handleSessionCompletionSideEffects = useHandleSessionCompletionSideEffectsCallback();
  const { Statsig } = useAppDependenciesContext();

  const callback = useCallback<CompleteSessionCallback>(
    async () => {
      // Important to get the appointment short ID before disconnecting from the
      // room
      const appointmentShortId = getInSessionAppointmentShortId();
      const room = await disconnectRoom();
      if (!room) return;

      setProviderState(ProviderState.COMPLETED);

      const shouldMarkAppointmentCompleted = Statsig.checkGate(markAppointmentCompletedMutationGate);

      if (shouldMarkAppointmentCompleted) {
        // Intentionally not awaited
        handleSessionCompletionSideEffects(appointmentShortId);
      } else {
        await dispatchSessionCompletionSideEffect(room);
      }
      return room;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return callback;
}
